import { useRef, useState } from "react"
import { useAuth } from "@/lib/auth"
import { Shell } from "@/components/Shell"
import { ACTIVITY_PRESETS, patchProfile, type Profile } from "@/lib/api"
import { useWeightUnit, toDisplay, fromDisplay, formatDisplay } from "@/lib/units"

/**
 * Profile page — form on the left, live-computed targets + goal preview on the right.
 *
 * Flow: You → Activity → Goal → Deficit. Goal weight comes BEFORE deficit so
 * the user thinks goal-first; deficit becomes the means-to-the-end.
 *
 * First save (no prior profile) → navigate to "/" so the user lands on the
 * now-populated dashboard. Subsequent saves stay in place with an inline pill.
 */
export function ProfilePage() {
  const auth = useAuth()
  const { unit, setUnit } = useWeightUnit()
  if (auth.status !== "authed") return null

  // Capture once at mount so onSubmit can detect first-time-save reliably
  // even after auth.refresh() repopulates auth.me.profile.
  const wasFirstSaveRef = useRef(auth.me.profile == null)

  const initial: Profile = auth.me.profile ?? {
    weight_kg: 75,
    height_cm: 175,
    age: 35,
    sex: "male",
    activity_multiplier: 1.55,
    deficit: 500,
    target_weight_kg: 0,
  }

  const [form, setForm] = useState<Profile>(initial)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pacing mode: deficit-driven (set kcal, see weeks) or timeline-driven
  // (set weeks, see kcal). Default to "deficit" because most users land
  // here knowing they want a particular daily cut. In "weeks" mode, the
  // form.deficit value is overridden at render time by a value computed
  // from weeksInput + goal; on save we persist the effective number.
  const [pacingMode, setPacingMode] = useState<"deficit" | "weeks">("deficit")

  // In timeline mode the user types weeks directly. Initialized lazily
  // when the user first enters timeline mode (see enterTimelineMode).
  // Empty string is a valid in-progress edit state.
  const [weeksInput, setWeeksInput] = useState<number | "">(0)

  // Effective deficit: in weeks mode, back-compute from goal + weeks via
  // Wishnofsky (7700 kcal/kg → 1100 kcal/week). In deficit mode, use the
  // form field directly. This is the source-of-truth that drives preview
  // AND save.
  const weeksAsNumber = typeof weeksInput === "number" ? weeksInput : 0
  const effectiveDeficit = (() => {
    if (pacingMode !== "weeks") return form.deficit
    const toLose = computeToLose(form)
    if (weeksAsNumber <= 0 || toLose <= 0) return 0
    return Math.max(0, Math.round((toLose / weeksAsNumber) * 7700 / 7))
  })()

  // Computed view of the profile using effectiveDeficit. Single
  // computePreview call so both panels stay consistent.
  const preview = computePreview({ ...form, deficit: effectiveDeficit })

  // Switching INTO timeline mode pre-fills with the current implied
  // weeks-to-goal so the toggle never lurches.
  function enterTimelineMode() {
    const implied =
      preview.weeksToGoal > 0 ? Math.max(1, Math.round(preview.weeksToGoal)) : 12
    setWeeksInput(implied)
    setPacingMode("weeks")
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      // Save the effective deficit (in weeks mode this is back-computed
      // from the user's chosen timeline; in deficit mode it equals form.deficit).
      await patchProfile({ ...form, deficit: effectiveDeficit })
      if (auth.status === "authed") await auth.refresh()
      if (wasFirstSaveRef.current) {
        // Mark so the dashboard can render a one-time welcome line if it wants.
        sessionStorage.setItem("fittrack:welcome", "1")
        wasFirstSaveRef.current = false
        // Match the custom router pattern in App.tsx: pushState + dispatch popstate.
        window.history.pushState({}, "", "/")
        window.dispatchEvent(new PopStateEvent("popstate"))
        return
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Save failed.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Shell
      currentPath="/profile"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/profile", label: "Profile" },
        { href: "/invites", label: "Invites" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-6 animate-ink-in">profile · daily targets</p>

        <h1
          className="font-display text-display tracking-tightest mb-3 animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          Tell us about <span className="italic text-moss">you</span>.
        </h1>
        <p
          className="font-sans text-base text-muted-foreground max-w-xl leading-relaxed mb-14 animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          We compute your daily calorie & macro targets using Mifflin-St Jeor,
          recommend a target weight using the WHO healthy-BMI band, and project
          your timeline from your chosen deficit (Wishnofsky, ≈7 700 kcal/kg).
        </p>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-16">
          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-10">
            <Section
              title="01 · You"
              right={
                <UnitToggle value={unit} onChange={setUnit} />
              }
            >
              <div className="grid grid-cols-3 gap-6">
                <NumberField
                  label="weight"
                  unit={unit}
                  value={toDisplay(form.weight_kg, unit)}
                  onChange={(v) =>
                    setForm({ ...form, weight_kg: fromDisplay(v, unit) })
                  }
                  step={0.1}
                />
                <NumberField
                  label="height"
                  unit="cm"
                  value={form.height_cm}
                  onChange={(v) => setForm({ ...form, height_cm: v })}
                  step={0.5}
                />
                <NumberField
                  label="age"
                  unit="yrs"
                  value={form.age}
                  onChange={(v) => setForm({ ...form, age: Math.round(v) })}
                  step={1}
                />
              </div>
              <div className="mt-6">
                <p className="eyebrow mb-3">sex</p>
                <SegmentedChoice
                  options={[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                  ]}
                  value={form.sex}
                  onChange={(v) => setForm({ ...form, sex: v })}
                />
              </div>
            </Section>

            <Section title="02 · Activity">
              <div className="space-y-2">
                {ACTIVITY_PRESETS.map((p) => {
                  const active = Math.abs(form.activity_multiplier - p.value) < 0.001
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() =>
                        setForm({ ...form, activity_multiplier: p.value })
                      }
                      className={`w-full text-left px-5 py-4 border transition-colors ${
                        active
                          ? "border-moss bg-moss/[0.04]"
                          : "border-border hover:border-foreground/40"
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-sans text-base tracking-tight">
                          {p.label}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground">
                          ×{p.value}
                        </span>
                      </div>
                      <span className="font-sans text-xs text-muted-foreground">
                        {p.desc}
                      </span>
                    </button>
                  )
                })}
              </div>
            </Section>

            <Section title="03 · Goal & Pace">
              {/* Target weight — own row */}
              <div className="flex flex-wrap items-end gap-6">
                <NumberField
                  label="target weight"
                  unit={unit}
                  value={toDisplay(form.target_weight_kg ?? 0, unit)}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      target_weight_kg: fromDisplay(v, unit),
                    })
                  }
                  step={0.1}
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      target_weight_kg: preview.recommendedTargetKg,
                    })
                  }
                  className="px-4 py-2 border border-border hover:border-moss text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  title="midpoint of WHO healthy BMI band (22 × height²)"
                >
                  use {formatDisplay(preview.recommendedTargetKg, unit)}
                </button>
              </div>

              {/* Inline BMI context */}
              <p className="font-sans text-xs text-muted-foreground mt-3 max-w-md leading-relaxed">
                bmi today{" "}
                <span className="font-mono text-foreground">
                  {preview.currentBMI.toFixed(1)}
                </span>{" "}
                · healthy band{" "}
                <span className="font-mono text-foreground">
                  {formatDisplay(preview.healthyMin, unit, 1).replace(
                    ` ${unit}`,
                    ""
                  )}
                  –{formatDisplay(preview.healthyMax, unit, 1)}
                </span>
                {preview.currentBMI > 0 && (
                  <>
                    {" · "}
                    <BMICategoryChip bmi={preview.currentBMI} />
                  </>
                )}
              </p>

              {/* Pace block — bi-directional. The active mode drives; the
                  other side renders as a derived display. Switching modes
                  keeps the implied value so nothing jumps. */}
              {preview.toLoseKg > 0 ? (
                <div className="mt-8">
                  <div className="flex items-baseline justify-between mb-4 gap-4">
                    <p className="eyebrow">pace · pick one to set</p>
                    <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
                      {toDisplay(preview.toLoseKg, unit).toFixed(1)} {unit} to go
                    </span>
                  </div>
                  <div className="inline-flex border border-border rounded overflow-hidden mb-5">
                    <button
                      type="button"
                      onClick={() => setPacingMode("deficit")}
                      className={`px-4 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                        pacingMode === "deficit"
                          ? "bg-foreground text-background"
                          : "bg-surface text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Set the deficit
                    </button>
                    <button
                      type="button"
                      onClick={enterTimelineMode}
                      className={`px-4 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                        pacingMode === "weeks"
                          ? "bg-foreground text-background"
                          : "bg-surface text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Set the timeline
                    </button>
                  </div>

                  <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-x-8 gap-y-4 items-end">
                    {/* Driver input */}
                    {pacingMode === "deficit" ? (
                      <NumberField
                        label="kcal/day below tdee"
                        unit="kcal"
                        value={form.deficit}
                        onChange={(v) =>
                          setForm({ ...form, deficit: Math.round(v) })
                        }
                        step={50}
                      />
                    ) : (
                      <NumberField
                        label="weeks to goal"
                        unit="wks"
                        value={
                          typeof weeksInput === "number" ? weeksInput : 0
                        }
                        onChange={(v) =>
                          setWeeksInput(Math.max(0, Math.round(v)))
                        }
                        step={1}
                      />
                    )}

                    {/* Equals — visual yoke between driver and derived */}
                    <span
                      aria-hidden
                      className="hidden sm:inline self-end pb-3 font-display text-3xl text-muted-foreground/40 italic"
                    >
                      =
                    </span>

                    {/* Derived value — italic moss, magazine treatment */}
                    {pacingMode === "deficit" ? (
                      <DerivedStat
                        label="weeks to goal"
                        value={
                          preview.weeksToGoal > 0
                            ? Math.round(preview.weeksToGoal).toString()
                            : "—"
                        }
                        unit={
                          preview.weeksToGoal > 0
                            ? `≈ ${(preview.weeksToGoal / 4.345).toFixed(1)} months`
                            : "set a deficit"
                        }
                      />
                    ) : (
                      <DerivedStat
                        label="kcal/day deficit"
                        value={
                          effectiveDeficit > 0
                            ? effectiveDeficit.toLocaleString()
                            : "—"
                        }
                        unit={
                          effectiveDeficit > 0
                            ? `${(effectiveDeficit / 1100).toFixed(2)} ${unit}/wk`
                            : "set a timeline"
                        }
                      />
                    )}
                  </div>

                  <p className="font-sans text-xs text-muted-foreground mt-4 max-w-md leading-relaxed">
                    Wishnofsky · 1 kg fat ≈ 7 700 kcal. Aggressive deficits
                    accelerate loss but compound muscle loss & adherence risk.
                  </p>

                  {preview.deficitExceedsSafe && (
                    <div className="mt-4 px-4 py-3 border-l-2 border-persimmon bg-persimmon/[0.04]">
                      <p className="font-sans text-xs text-persimmon leading-relaxed">
                        <span className="font-mono uppercase tracking-wider">
                          warning
                        </span>{" "}
                        · this pace would drop daily calories below your BMR
                        ({preview.bmrFloor.toLocaleString()} kcal) or exceed
                        1 000 kcal/day deficit. Recommended max:{" "}
                        <span className="font-mono">
                          {preview.maxSafeDeficit} kcal
                        </span>{" "}
                        (~{(preview.maxSafeDeficit / 1100).toFixed(2)} kg/week)
                        {pacingMode === "weeks" && (
                          <>
                            {" "}— try a longer timeline (
                            <span className="font-mono">
                              ≥{Math.ceil(
                                (preview.toLoseKg * 1100) / preview.maxSafeDeficit,
                              )}
                            </span>{" "}
                            weeks).
                          </>
                        )}
                        .
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-8 px-5 py-4 border-l-2 border-moss bg-moss/[0.04] max-w-md">
                  <p className="font-sans text-sm leading-relaxed">
                    You're at or below your target weight. 🎯 No pace to set —
                    your calorie target equals your TDEE.
                  </p>
                </div>
              )}
            </Section>

            <div className="pt-2 flex items-center gap-4">
              <button
                type="submit"
                disabled={saving}
                className="bg-foreground text-background px-7 py-3.5 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-50"
              >
                {saving
                  ? "Saving…"
                  : wasFirstSaveRef.current
                  ? "Save & continue →"
                  : "Save profile"}
              </button>
              {saved && (
                <span className="eyebrow text-moss">saved · targets updated</span>
              )}
              {error && (
                <span className="font-sans text-sm text-persimmon">{error}</span>
              )}
            </div>
          </form>

          {/* Preview */}
          <aside className="lg:sticky lg:top-8 self-start">
            <p className="eyebrow mb-3">live preview · daily targets</p>
            <div className="card-surface px-6 sm:px-8 py-8">
              <div className="space-y-6">
                <MacroLine
                  label="calories"
                  value={preview.calories}
                  unit="kcal"
                  emphasized
                />
                <Divider />
                <MacroLine label="protein" value={preview.protein} unit="g" />
                <MacroLine label="fiber" value={preview.fiber} unit="g" />
                <MacroLine label="carbs" value={preview.carbs} unit="g" />
                <MacroLine label="sugar cap" value={25} unit="g" tone="warn" />
              </div>
              <div className="rule my-6" />
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="eyebrow mb-1">bmr</p>
                  <p className="font-mono">
                    {Math.round(preview.bmr)} kcal
                  </p>
                </div>
                <div>
                  <p className="eyebrow mb-1">tdee</p>
                  <p className="font-mono">
                    {Math.round(preview.tdee)} kcal
                  </p>
                </div>
              </div>
            </div>

            {/* Compact pace summary — the bidirectional driver lives inside
                Section 03 now; this card is just the at-a-glance receipt. */}
            {preview.toLoseKg > 0 && preview.weeklyLossKg > 0 && (
              <div className="card-surface px-6 sm:px-8 py-5 mt-6">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="eyebrow">weekly loss rate</span>
                  <span className="font-mono text-sm">
                    {toDisplay(preview.weeklyLossKg, unit).toFixed(2)} {unit}/wk
                  </span>
                </div>
              </div>
            )}

            <p className="eyebrow text-muted-foreground mt-4 max-w-xs">
              targets update live as you edit · saved on submit
            </p>
          </aside>
        </div>

        {/* Footer-ish CTA — invites are a once-in-a-while task, not a daily
            one, so they live under the fold on Profile rather than in the
            top nav of every page. */}
        <div className="mt-20">
          <div className="rule mb-10" />
          <a
            href="/invites"
            className="group block max-w-2xl"
            aria-label="Open the invites guestbook"
          >
            <p className="eyebrow mb-3">the guestbook</p>
            <h3 className="font-display text-3xl tracking-tightest mb-3">
              Bring someone{" "}
              <span className="italic text-moss">in</span>
              <span aria-hidden className="inline-block ml-3 transition-transform group-hover:translate-x-1">
                →
              </span>
            </h3>
            <p className="font-sans text-sm text-muted-foreground max-w-md">
              Mint a single-use invite code for a friend. Revocable anytime
              before they claim it.
            </p>
          </a>
        </div>
      </div>
    </Shell>
  )
}

// ---- small primitives ----

function Section({
  title,
  children,
  right,
}: {
  title: string
  children: React.ReactNode
  right?: React.ReactNode
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-5">
        <p className="eyebrow">{title}</p>
        {right}
      </div>
      {children}
    </section>
  )
}

function UnitToggle({
  value,
  onChange,
}: {
  value: "kg" | "lb"
  onChange: (u: "kg" | "lb") => void
}) {
  return (
    <div
      className="inline-flex border border-border rounded overflow-hidden"
      role="radiogroup"
      aria-label="Weight unit"
    >
      {(["kg", "lb"] as const).map((u) => {
        const active = u === value
        return (
          <button
            key={u}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(u)}
            className={`px-3 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors ${
              active
                ? "bg-foreground text-background"
                : "bg-surface text-muted-foreground hover:text-foreground"
            }`}
          >
            {u}
          </button>
        )
      })}
    </div>
  )
}

function NumberField({
  label,
  unit,
  value,
  onChange,
  step,
}: {
  label: string
  unit: string
  value: number
  onChange: (v: number) => void
  step: number
}) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
        {label}
      </span>
      <div className="flex items-baseline gap-2 border-b border-border focus-within:border-moss transition-colors py-2">
        <input
          type="number"
          inputMode="decimal"
          step={step}
          value={Number.isFinite(value) && value > 0 ? value : ""}
          onChange={(e) => {
            const v = parseFloat(e.target.value)
            onChange(Number.isFinite(v) ? v : 0)
          }}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none font-display text-2xl tracking-tight num text-foreground"
        />
        <span className="font-mono text-xs text-muted-foreground">{unit}</span>
      </div>
    </label>
  )
}

function SegmentedChoice<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex border border-border rounded overflow-hidden">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-5 py-2.5 font-sans text-sm tracking-tight transition-colors ${
              active
                ? "bg-foreground text-background"
                : "bg-surface text-muted-foreground hover:text-foreground"
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function MacroLine({
  label,
  value,
  unit,
  tone = "default",
  emphasized = false,
}: {
  label: string
  value: number
  unit: string
  tone?: "default" | "warn"
  emphasized?: boolean
}) {
  const color = tone === "warn" ? "text-persimmon" : "text-foreground"
  const size = emphasized ? "text-5xl sm:text-6xl" : "text-3xl"
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="eyebrow">{label}</span>
      <div className="flex items-baseline gap-2">
        <span
          className={`font-display ${size} ${color} num tracking-tightest`}
        >
          {value.toLocaleString()}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{unit}</span>
      </div>
    </div>
  )
}

function Divider() {
  return <div className="h-px w-full bg-border" />
}

/**
 * DerivedStat — the "you'll get" side of the pacing toggle. Italic moss
 * numeral so it reads as the consequence (not the input), with a tight
 * mono caption below. Matches the typographic hierarchy of NumberField so
 * the row reads as input = output.
 */
function DerivedStat({
  label,
  value,
  unit,
}: {
  label: string
  value: string
  unit: string
}) {
  return (
    <div className="block">
      <span className="eyebrow block mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
        {label}
      </span>
      <div className="border-b border-transparent py-2">
        <p className="font-display text-2xl tracking-tight italic text-moss num leading-none">
          {value}
        </p>
        <p className="font-mono text-[11px] text-muted-foreground mt-1 tabular-nums">
          {unit}
        </p>
      </div>
    </div>
  )
}

function BMICategoryChip({ bmi }: { bmi: number }) {
  let label = "healthy"
  let color = "text-moss"
  if (bmi < 18.5) {
    label = "underweight"
    color = "text-persimmon"
  } else if (bmi >= 25 && bmi < 30) {
    label = "overweight"
    color = "text-persimmon"
  } else if (bmi >= 30) {
    label = "obese"
    color = "text-persimmon"
  }
  return (
    <span className={`font-mono uppercase tracking-wider ${color}`}>
      {label}
    </span>
  )
}

// ---- local target + recommendation computation (mirrors backend exactly) ----
// Keep these in sync with internal/targets/{compute.go,recommend.go}.

// Standalone "how much to lose" used by the pacing toggle to back-compute
// the deficit from a chosen timeline. Mirrors the goal-pick logic inside
// computePreview but factored so the timeline math can run before we've
// built a full preview object.
function computeToLose(p: Profile): number {
  const heightM = p.height_cm / 100
  const h2 = heightM * heightM
  const recommendedTargetKg = h2 > 0 ? Math.round(22 * h2 * 10) / 10 : 0
  const goalKg =
    p.target_weight_kg && p.target_weight_kg > 0
      ? p.target_weight_kg
      : recommendedTargetKg
  return Math.max(0, p.weight_kg - goalKg)
}

function computePreview(p: Profile) {
  // Mifflin-St Jeor BMR + TDEE + deficit-adjusted daily calories.
  const bmr =
    10 * p.weight_kg +
    6.25 * p.height_cm -
    5 * p.age +
    (p.sex === "male" ? 5 : -161)
  const tdee = bmr * p.activity_multiplier
  const cals = Math.max(0, Math.round(tdee) - p.deficit)

  // Healthy-BMI band (WHO).
  const heightM = p.height_cm / 100
  const h2 = heightM * heightM
  const recommendedTargetKg = h2 > 0 ? Math.round(22 * h2 * 10) / 10 : 0
  const healthyMin = h2 > 0 ? Math.round(18.5 * h2 * 10) / 10 : 0
  const healthyMax = h2 > 0 ? Math.round(24.9 * h2 * 10) / 10 : 0
  const currentBMI = h2 > 0 && p.weight_kg > 0
    ? Math.round((p.weight_kg / h2) * 10) / 10
    : 0

  // Wishnofsky: 1 kg fat ≈ 7700 kcal.
  const weeklyLossKg =
    p.deficit > 0 ? Math.round(((p.deficit * 7) / 7700) * 100) / 100 : 0

  // Effective target: user-set wins, else BMI-22 suggestion.
  const goalKg =
    p.target_weight_kg && p.target_weight_kg > 0
      ? p.target_weight_kg
      : recommendedTargetKg
  const toLoseKg = Math.max(0, p.weight_kg - goalKg)
  const weeksToGoal =
    toLoseKg > 0 && weeklyLossKg > 0
      ? Math.round((toLoseKg / weeklyLossKg) * 10) / 10
      : 0

  // Safety: never drop calories below BMR; cap deficit at 1000 kcal/day.
  const maxByBMR = Math.max(0, Math.round(tdee - bmr))
  const maxSafeDeficit = Math.min(1000, maxByBMR)
  const deficitExceedsSafe = p.deficit > maxSafeDeficit

  return {
    bmr,
    tdee,
    calories: cals,
    protein: Math.round(1.8 * p.weight_kg),
    fiber: Math.round((14 * cals) / 1000),
    carbs: Math.round((0.45 * cals) / 4),
    currentBMI,
    recommendedTargetKg,
    healthyMin,
    healthyMax,
    weeklyLossKg,
    toLoseKg,
    weeksToGoal,
    maxSafeDeficit,
    deficitExceedsSafe,
    bmrFloor: Math.round(bmr),
  }
}
